import { authStore } from "@/lib/authStore";
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, useTheme } from '@/App';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Database, Sun, Moon, Shield, ArrowLeft, Building2, FileText,
  MessageSquare, ExternalLink, History
} from 'lucide-react';
import { setActiveTenant } from '@/lib/tenantImpersonation';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function getAuthHeaders() {
  var token = authStore.getAccessToken();
  return token ? { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' } : {};
}

const TenantDirectoryPage = function() {
  var { user } = useAuth();
  var { theme, toggleTheme } = useTheme();
  var navigate = useNavigate();
  var [tenants, setTenants] = useState([]);
  var [loading, setLoading] = useState(true);
  var [audit, setAudit] = useState([]);
  var [auditLoading, setAuditLoading] = useState(false);

  // Strict access guard. Only platform super-admin (is_admin && no organization_id).
  var isSuperAdmin = user && user.is_admin && !user.organization_id;

  useEffect(function() {
    if (!isSuperAdmin) return;
    (async function() {
      setLoading(true);
      try {
        var r = await fetch(API_URL + '/admin/tenants', { headers: getAuthHeaders() });
        if (!r.ok) {
          var err = await r.json();
          toast.error((err && err.detail) || 'Failed to load tenants');
          return;
        }
        var data = await r.json();
        setTenants(data.tenants || []);
      } catch (e) {
        toast.error('Network error loading tenants');
      } finally {
        setLoading(false);
      }
    })();
  }, [isSuperAdmin]);

  async function loadAudit() {
    setAuditLoading(true);
    try {
      var r = await fetch(API_URL + '/admin/tenants/audit-log?limit=50', { headers: getAuthHeaders() });
      if (r.ok) {
        var data = await r.json();
        setAudit(data.events || []);
      }
    } finally {
      setAuditLoading(false);
    }
  }

  function operateAs(orgId) {
    setActiveTenant(orgId);
    navigate('/knowledge-base?as_tenant=' + encodeURIComponent(orgId));
  }

  if (!user) {
    return null;
  }

  if (!isSuperAdmin) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
        <div className="max-w-md text-center space-y-3" data-testid="tenants-access-denied">
          <Shield className="h-10 w-10 text-amber-500 mx-auto" />
          <h1 className="text-xl font-semibold">Tenant directory is super-admin only</h1>
          <p className="text-sm text-muted-foreground">
            Tenant admins cannot list other tenants. If you need cross-tenant access,
            contact your platform super-admin.
          </p>
          <Link to="/admin"><Button size="sm" className="rounded-full">Back to Admin</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b border-border/50 bg-card px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-4 w-4" /> Back to Admin
            </Link>
            <div className="h-6 w-px bg-border/50" />
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-primary" />
              <h1 className="font-semibold text-lg">Tenant Directory</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={toggleTheme} className="p-2 rounded-lg hover:bg-secondary transition-colors">
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto w-full px-6 py-8 space-y-6">
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 text-sm" data-testid="tenants-impersonation-notice">
          <div className="flex items-start gap-2">
            <Shield className="h-4 w-4 text-amber-500 mt-0.5" />
            <div>
              <p className="font-medium">Impersonation is audited</p>
              <p className="text-muted-foreground text-xs mt-1">
                Choosing &quot;Operate as this tenant&quot; switches every Knowledge Base call to <code className="rounded bg-muted px-1">?as_tenant=&lt;org_id&gt;</code>.
                Each call is recorded in the audit log below.
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-border/50 bg-card overflow-hidden">
          <div className="px-5 py-3 border-b border-border/50 flex items-center justify-between">
            <h2 className="font-medium" data-testid="tenants-section-title">Tenants ({tenants.length})</h2>
          </div>
          {loading ? (
            <div className="px-5 py-8 text-sm text-muted-foreground" data-testid="tenants-loading">Loading…</div>
          ) : tenants.length === 0 ? (
            <div className="px-5 py-8 text-sm text-muted-foreground" data-testid="tenants-empty">No tenants registered yet.</div>
          ) : (
            <div className="divide-y divide-border/30" data-testid="tenants-list">
              {tenants.map(function(t) {
                return (
                  <div key={t.id} className="px-5 py-4 flex items-center justify-between gap-4 hover:bg-secondary/20 transition-colors">
                    <div>
                      <div className="font-medium" data-testid={'tenant-name-' + t.id}>{t.name || t.id}</div>
                      <div className="text-xs text-muted-foreground mt-0.5"><code>{t.id}</code></div>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> {t.doc_count || 0} docs</span>
                      <span className="flex items-center gap-1.5"><MessageSquare className="h-3.5 w-3.5" /> {t.query_count || 0} queries</span>
                    </div>
                    <Button
                      size="sm"
                      className="rounded-full"
                      onClick={function() { operateAs(t.id); }}
                      data-testid={'tenant-operate-' + t.id}
                    >
                      Operate as this tenant <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-border/50 bg-card overflow-hidden" data-testid="tenants-audit-section">
          <div className="px-5 py-3 border-b border-border/50 flex items-center justify-between">
            <h2 className="font-medium flex items-center gap-2"><History className="h-4 w-4" /> Impersonation Audit Log</h2>
            <Button
              size="sm"
              variant="outline"
              className="rounded-full text-xs h-7"
              onClick={loadAudit}
              disabled={auditLoading}
              data-testid="tenants-audit-refresh"
            >
              {auditLoading ? 'Loading…' : 'Refresh'}
            </Button>
          </div>
          {audit.length === 0 ? (
            <div className="px-5 py-6 text-xs text-muted-foreground">
              {auditLoading ? 'Loading…' : 'No impersonation events recorded yet. Click Refresh.'}
            </div>
          ) : (
            <div className="divide-y divide-border/30 max-h-96 overflow-auto text-xs">
              {audit.map(function(e) {
                return (
                  <div key={e.id} className="px-5 py-2.5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <code className="rounded bg-muted px-1.5 py-0.5">{e.method}</code>
                      <span className="text-muted-foreground">{e.endpoint}</span>
                      <span>→ <strong>{e.target_org_id}</strong></span>
                    </div>
                    <div className="flex items-center gap-3 text-muted-foreground">
                      <span>{e.actor_email}</span>
                      <span>{(e.timestamp || '').slice(0, 19).replace('T', ' ')} UTC</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TenantDirectoryPage;
